import { useCallback, useRef, useState } from 'react';

import { api, type RecruiterDetail, type RecruiterWeekDetail } from './api';
import { RevenueArea } from './components/Charts';
import { UnifiedTimeFilter } from './components/UnifiedTimeFilter';
import { useTimeFilter } from './context/TimeFilterContext';
import {
  Card,
  CardHeader,
  Cell,
  ExportButton,
  Kpi,
  Row,
  SearchBar,
  Spinner,
  Table,
} from './components/Ui';
import { useAsyncData } from './hooks/useAsyncData';
import { useSortedRows } from './hooks/useSortedRows';
import { findCurrentTier, TierGauge } from './components/PlanTierCard';
import { hours, marginPercent, money, moneyCompact, rate, shortDate } from './format';
import { Shell, type ShellProps } from './Shell';

type RecruiterContractor = RecruiterDetail['contractors'][number];

function RoleStartDateField({
  userId,
  initial,
  onSaved,
}: {
  userId: string;
  initial: string | null;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initial ?? '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const save = async () => {
    setSaving(true);
    try {
      const value = draft || null;
      await api.updateUser(userId, { roleEffectiveDate: value });
      onSaved();
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div>
        <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
          Role Start Date
        </span>
        <div className="mt-0.5 flex items-center gap-2">
          <input
            ref={inputRef}
            type="date"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={saving}
            className="rounded border border-edge bg-surface-base px-2 py-1 text-sm text-ink focus:border-profit focus:outline-none focus:ring-1 focus:ring-profit/30"
          />
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            className="rounded bg-profit px-2 py-1 text-xs font-medium text-white hover:bg-profit/90 disabled:opacity-50"
          >
            {saving ? '…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => {
              setDraft(initial ?? '');
              setEditing(false);
            }}
            disabled={saving}
            className="text-xs text-ink-faint hover:text-ink"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
        Role Start Date
      </span>
      <p className="mt-0.5 flex items-center gap-2 font-medium text-ink">
        <span>{initial ? shortDate(initial) : 'Not set'}</span>
        <button
          type="button"
          onClick={() => {
            setEditing(true);
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          className="text-xs text-ink-faint hover:text-profit"
          title="Edit role start date"
        >
          ✎
        </button>
      </p>
    </div>
  );
}

/** Inline week-detail expansion for a single contractor row. */
function WeekRows({
  userId,
  workerId,
  planId,
}: {
  userId: string;
  workerId: string;
  planId?: string | undefined;
}) {
  const tf = useTimeFilter();
  const { data, loading } = useAsyncData<RecruiterWeekDetail[]>(
    () => api.recruiterWeeks(userId, workerId, tf.toDateRange(), planId),
    [userId, workerId, tf.selection, planId],
    'Could not load weekly detail.',
  );

  if (loading) {
    return (
      <tr>
        <td colSpan={10} className="px-5 py-2 text-xs text-ink-faint italic">
          Loading weeks…
        </td>
      </tr>
    );
  }

  if (!data || data.length === 0) {
    return (
      <tr>
        <td colSpan={10} className="px-5 py-2 text-xs text-ink-faint italic">
          No weekly data in this range.
        </td>
      </tr>
    );
  }

  return (
    <>
      {data.map((w) => (
        <tr key={w.weekEnding} className="bg-surface-overlay/50">
          <td className="px-3 py-1 pl-8 text-xs text-ink-faint">{shortDate(w.weekEnding)}</td>
          <td />
          <td />
          <td />
          <td />
          <td className="px-3 py-1 text-right font-mono text-xs text-ink-muted">
            {hours(w.hours)}
          </td>
          <td className="px-3 py-1 text-right font-mono text-xs text-ink-muted">
            {Number(w.hours) > 0 ? rate((Number(w.grossProfit) / Number(w.hours)).toFixed(4)) : '—'}
          </td>
          <td className="px-3 py-1 text-right font-mono text-xs text-ink-muted">
            {money(w.grossProfit)}
          </td>
          <td className="px-3 py-1 text-right font-mono text-xs text-ink-muted">
            {money(w.creditedGp)}
          </td>
        </tr>
      ))}
    </>
  );
}

export function Recruiter(props: ShellProps & { userId: string }) {
  const { userId, navigate } = props;
  const [search, setSearch] = useState('');
  const tf = useTimeFilter();
  const [expandedWorker, setExpandedWorker] = useState<string | null>(null);

  // Extract planId from the URL query string (e.g. ?planId=abc)
  const planId = (() => {
    const hash = window.location.hash;
    const qIdx = hash.indexOf('?');
    if (qIdx < 0) return undefined;
    const params = new URLSearchParams(hash.slice(qIdx));
    return params.get('planId') ?? undefined;
  })();

  const { data, error, reload } = useAsyncData(
    () => api.recruiter(userId, tf.toDateRange(), planId),
    [userId, tf.selection, planId],
    'Could not load recruiter.',
  );

  const filteredContractors =
    data !== null
      ? data.contractors.filter(
          (c) =>
            search === '' ||
            c.name.toLowerCase().includes(search.toLowerCase()) ||
            c.clientName.toLowerCase().includes(search.toLowerCase()),
        )
      : [];

  const accessor = useCallback((row: RecruiterContractor, key: string): unknown => {
    const map: Record<string, unknown> = {
      name: row.name,
      client: row.clientName,
      type: row.workerType,
      share: row.share,
      weeks: row.weeks,
      hours: row.hours,
      gpPerHour: row.grossProfitPerHour,
      grossProfit: row.grossProfit,
      creditedGp: row.creditedGp,
    };
    return map[key];
  }, []);

  const { sorted, sortKey, sortDirection, onSort } = useSortedRows(filteredContractors, accessor);

  if (error !== null) {
    return (
      <Shell {...props} title="Commission Earner">
        <Card className="px-5 py-8">
          <p className="text-sm text-caution">{error}</p>
        </Card>
      </Shell>
    );
  }

  if (data === null) {
    return (
      <Shell {...props} title="Commission Earner">
        <Card>
          <Spinner label="Loading commission earner detail…" />
        </Card>
      </Shell>
    );
  }

  const totalGp = data.contractors.reduce((s, c) => s + Number(c.grossProfit), 0).toFixed(4);
  const totalCreditedGp = data.contractors.reduce((s, c) => s + Number(c.creditedGp), 0).toFixed(4);
  const totalHours = data.contractors.reduce((s, c) => s + Number(c.hours), 0).toFixed(4);
  const totalBilled = data.contractors.reduce((s, c) => s + Number(c.billed), 0).toFixed(4);
  const gpNum = Number(totalCreditedGp);

  const currentTier = data.tiers.length > 0 ? findCurrentTier(data.tiers, gpNum) : null;
  const commissionEarned = data.commission;

  const trendPoints = data.trend.map((t) => ({
    label: t.weekEnding,
    billed: t.grossProfit,
    grossProfit: t.grossProfit,
  }));

  return (
    <Shell {...props} title={data.name}>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={() => navigate('recruiters')}
            className="mb-2 text-xs text-ink-faint transition-colors hover:text-ink"
          >
            ← Back to commission earners
          </button>
          <h1 className="text-xl font-semibold text-ink">{data.name}</h1>
          <p className="mt-1 text-sm text-ink-faint">
            {data.role === 'salesperson' ? 'Salesperson' : 'Recruiter'} commission detail
            {data.planName && (
              <span className="ml-2 inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                {data.planName}
              </span>
            )}
          </p>
        </div>
        <UnifiedTimeFilter />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-6 rounded-lg bg-surface-raised px-4 py-3 text-sm">
        <div>
          <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
            Role
          </span>
          <p className="mt-0.5 font-medium capitalize text-ink">{data.role}</p>
        </div>
        <RoleStartDateField
          userId={userId}
          initial={data.roleEffectiveDate}
          onSaved={() => reload()}
        />
        <div>
          <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
            Elevated Window
          </span>
          <p className="mt-0.5 font-medium text-ink">
            {data.roleEffectiveDate
              ? (() => {
                  const start = new Date(data.roleEffectiveDate);
                  const end = new Date(start);
                  end.setMonth(end.getMonth() + 18);
                  return end > new Date()
                    ? `Until ${shortDate(end.toISOString().slice(0, 10))}`
                    : 'Expired';
                })()
              : 'N/A'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <Kpi
          label="Credited GP"
          value={moneyCompact(totalCreditedGp)}
          exact={money(totalCreditedGp)}
          tone="profit"
          delay={0}
        />
        <Kpi
          label="Commission earned"
          value={moneyCompact(commissionEarned)}
          exact={money(commissionEarned)}
          tone="revenue"
          delay={20}
        />
        <Kpi
          label="Revenue"
          value={moneyCompact(totalBilled)}
          exact={money(totalBilled)}
          tone="revenue"
          delay={40}
        />
        <Kpi label="Hours" value={hours(totalHours)} delay={80} />
        <Kpi label="Contractors" value={String(data.contractors.length)} delay={120} />
        <Kpi
          label="GP margin"
          value={marginPercent(totalGp, totalBilled)}
          tone="profit"
          delay={160}
        />
      </div>

      {trendPoints.length > 0 && (
        <Card className="mt-4" delay={180}>
          <CardHeader title="Weekly gross profit" subtitle="GP trend by pay period" />
          <RevenueArea points={trendPoints} />
        </Card>
      )}

      {data.tiers.length > 0 && (
        <Card className="mt-4" delay={200}>
          <CardHeader
            title="Commission tier ladder"
            subtitle={
              currentTier !== null
                ? `Current tier: ${(Number(currentTier.rate) * 100).toFixed(0)}% on ${data.tierMode === 'marginal' ? 'marginal' : 'total'} gross profit`
                : ''
            }
          />
          <div className="px-5 py-4">
            <TierGauge tiers={data.tiers} currentGp={gpNum} />
          </div>
        </Card>
      )}

      <Card className="mt-4" delay={240}>
        <CardHeader
          title="Contractor assignments"
          subtitle="Gross profit attributed to this recruiter, by contractor"
          aside={
            <div className="flex items-center gap-3">
              <div className="w-48">
                <SearchBar value={search} onChange={setSearch} placeholder="Filter contractors…" />
              </div>
              <ExportButton
                data={sorted.map((c) => ({
                  name: c.name,
                  client: c.clientName,
                  type: c.workerType === 'contractor_1099' ? '1099' : 'W2',
                  share: `${(Number(c.share) * 100).toFixed(0)}%`,
                  weeks: c.weeks,
                  hours: hours(c.hours),
                  gpPerHour: rate(c.grossProfitPerHour),
                  grossProfit: money(c.grossProfit),
                  creditedGp: money(c.creditedGp),
                }))}
                filename={`recruiter-${data.name.toLowerCase().replace(/\s+/g, '-')}`}
                headers={[
                  { key: 'name', label: 'Contractor' },
                  { key: 'client', label: 'Customer' },
                  { key: 'type', label: 'Type' },
                  { key: 'share', label: 'Share' },
                  { key: 'weeks', label: 'Weeks' },
                  { key: 'hours', label: 'Hours' },
                  { key: 'gpPerHour', label: 'GP/h' },
                  { key: 'grossProfit', label: 'Gross Profit' },
                  { key: 'creditedGp', label: 'Credited GP' },
                ]}
              />
            </div>
          }
        />
        <Table
          head={[
            { label: 'Contractor', sortKey: 'name' },
            { label: 'Customer', sortKey: 'client' },
            { label: 'Type', sortKey: 'type' },
            { label: 'Share', align: 'right', sortKey: 'share' },
            { label: 'Weeks', align: 'right', sortKey: 'weeks' },
            { label: 'Hours', align: 'right', sortKey: 'hours' },
            { label: 'GP/h', align: 'right', sortKey: 'gpPerHour' },
            { label: 'Gross profit', align: 'right', sortKey: 'grossProfit' },
            { label: 'Credited GP', align: 'right', sortKey: 'creditedGp' },
          ]}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={onSort}
        >
          {sorted.map((c) => {
            const isExpanded = expandedWorker === c.id;
            return (
              <>
                <Row key={c.id} onClick={() => setExpandedWorker(isExpanded ? null : c.id)}>
                  <Cell>
                    <span className="text-ink cursor-pointer hover:text-profit transition-colors">
                      {isExpanded ? '▾ ' : '▸ '}
                      {c.name}
                    </span>
                  </Cell>
                  <Cell>{c.clientName}</Cell>
                  <Cell muted>{c.workerType === 'contractor_1099' ? '1099' : 'W2'}</Cell>
                  <Cell align="right" mono muted>
                    {(Number(c.share) * 100).toFixed(0)}%
                  </Cell>
                  <Cell align="right" mono>
                    {c.weeks}
                  </Cell>
                  <Cell align="right" mono>
                    {hours(c.hours)}
                  </Cell>
                  <Cell align="right" mono>
                    {rate(c.grossProfitPerHour)}
                  </Cell>
                  <Cell align="right" mono>
                    {money(c.grossProfit)}
                  </Cell>
                  <Cell align="right" mono>
                    <span className="text-profit">{money(c.creditedGp)}</span>
                  </Cell>
                </Row>
                {isExpanded && <WeekRows userId={userId} workerId={c.id} planId={planId} />}
              </>
            );
          })}
        </Table>
      </Card>
    </Shell>
  );
}

/* TierDisplay, findCurrentTier, and TierGauge are imported from components/PlanTierCard. */
